import type { FormInstance, FormRules } from 'element-plus'
import { ApiStatus, UCenterServices } from 'network'
import { useUserInfoStore } from 'store'

import { useI18n } from 'i18n-utils'
import { deepClone, encryptPassword } from 'utils'

import { CommonConfirmSecurityAuthType } from 'network/src/constants/security'
import { useCommonFormRules } from '../hooks/use-form-rules'
import { UiUtils } from '../ui-utils/ui-utils'
import { PasswordAuthSource } from './use-security-auth'

export type PasswordFormData = {
  oldPassword?: string
  password?: string
  okPassword?: string
  tradePwd?: string
  confirmTradePwd?: string
  originTradePwd?: string
}

const FormDataMap: Record<PasswordAuthSource, PasswordFormData> = {
  [PasswordAuthSource.Login]: {
    oldPassword: '',
    password: '',
    okPassword: '',
  },
  [PasswordAuthSource.Trade]: {
    tradePwd: '',
    confirmTradePwd: '',
    originTradePwd: '',
  },
}

export const useSecuritySettingPassword = (passwordSource: PasswordAuthSource) => {
  const userInfoStore = useUserInfoStore()
  const { t } = useI18n()
  const formInstance = ref<FormInstance>()
  const visible = ref(false)
  const formData = ref<PasswordFormData>(deepClone(FormDataMap[passwordSource]))
  const loading = ref(false)
  const countDown = ref(-1)
  const lastVerifyResult = ref<ApiResponse.SafetyAuthResponse>()

  const commonRules = useCommonFormRules()

  const clearFormData = () => {
    formData.value = deepClone(FormDataMap[passwordSource])

    formInstance.value?.clearValidate()
  }

  const rules = computed<FormRules<PasswordFormData>>(() => {
    return {
      oldPassword: [{ required: true, message: t('请输入当前密码'), trigger: 'change' }],
      password: commonRules.pwdRules.pwd,
      okPassword: [
        { required: true, message: t('请输入确认密码'), trigger: 'change' },
        {
          trigger: 'change',
          validator: (rule, value, callback) => {
            if (value !== formData.value.password) {
              callback(new Error(t('两次输入的密码不一致')))
            }
            callback()
          },
        },
      ],
      tradePwd: [
        { required: true, message: formData.value.originTradePwd ? t('请输入新支付密码') : t('请输入支付密码'), trigger: 'change' },
        {
          min: 6,
          max: 6,
          message: t('支付密码长度为6位数字'),
          trigger: 'change',
        },
      ],
      confirmTradePwd: [
        { required: true, message: formData.value.originTradePwd ? t('请确认新支付密码') : t('请确认支付密码'), trigger: 'change' },
        {
          trigger: 'change',
          validator: (rule, value, callback) => {
            if (value !== formData.value.tradePwd) {
              callback(new Error(t('两次输入的密码不一致')))
            }
            callback()
          },
        },
      ],
      originTradePwd: [
        { required: true, message: t('请输入当前支付密码'), trigger: 'change' },
        {
          min: 6,
          max: 6,
          message: t('支付密码长度为6位数字'),
          trigger: 'change',
        },
      ],
    }
  })

  const onOpen = () => {
    visible.value = true
  }

  const onClose = () => {
    visible.value = false
    formData.value = deepClone(FormDataMap[passwordSource])
    formInstance.value?.clearValidate()
    formInstance.value?.resetFields()
  }

  const updatePassword = async () => {
    const res = await UCenterServices.updateLoginPassword({
      oldPassword: encryptPassword(formData.value.oldPassword!),
      password: encryptPassword(formData.value.password!),
      type: CommonConfirmSecurityAuthType.ChangeLoginPwd,
      tokenVerify: lastVerifyResult.value?.tokenVerify || '',
    })
    if (res.status === ApiStatus.Success) {
      UiUtils.showSuccess(t('修改成功'))
      userInfoStore.logoutAndToHome()
      onClose()
    }
    return res.status === ApiStatus.Success
  }

  const updateTradePassword = async () => {
    if (userInfoStore.isSetTradePassword) {
      // 重置支付密码
      const res = await UCenterServices.resetTradePassword({
        tradePwd: encryptPassword(formData.value.tradePwd!),
        confirmTradePwd: encryptPassword(formData.value.confirmTradePwd!),
        type: CommonConfirmSecurityAuthType.ResetPayPassword,
        tokenVerify: lastVerifyResult.value?.tokenVerify || '',
      })
      if (res.status === ApiStatus.Success) {
        UiUtils.showSuccess(t('重置成功'))
        userInfoStore.refreshUserInfo()
        onClose()
      }
      return res.status === ApiStatus.Success
    }
    // 设置支付密码
    const res = await UCenterServices.setTradePassword({
      tradePwd: encryptPassword(formData.value.tradePwd!),
      confirmTradePwd: encryptPassword(formData.value.confirmTradePwd!),
    })
    if (res.status === ApiStatus.Success) {
      UiUtils.showSuccess(t('支付密码设置成功'))
      userInfoStore.refreshUserInfo()
      onClose()
    }
    return res.status === ApiStatus.Success
  }

  const onSubmit = async () => {
    const valid = await formInstance.value?.validate()
    if (!valid) return
    loading.value = true
    if (passwordSource === PasswordAuthSource.Login) {
      const res = await updatePassword()
      loading.value = false
      return res
    } else {
      const res = await updateTradePassword()
      loading.value = false
      return res
    }
  }

  const onSecurityAuthSuccess = async (res: ApiResponse.SafetyAuthResponse | void) => {
    lastVerifyResult.value = res || undefined
    onOpen()
  }

  return {
    rules,
    formInstance,
    visible,
    formData,
    loading,
    countDown,
    clearFormData,
    onOpen,
    onClose,
    onSubmit,
    onSecurityAuthSuccess,
  }
}
